/**
 * Organization Service
 * Handles all API calls related to organizations
 */

import {
  MemberRole,
  Organization,
  OrganizationInvitation,
  OrganizationMember,
} from "../types/organization";
import { offlineSupabase } from "../utils/offlineSupabaseProxy";
import { supabase } from "../utils/supabaseClient";
import { triggerTeableOnboarding } from "../utils/teableOnboarding";

/**
 * Create a new organization
 */
export const createOrganization = async (
  name: string,
  description?: string,
  tenantType: 'personal' | 'organisation' = 'organisation'
): Promise<Organization | null> => {
  try {
    console.log('[OrganizationService] 🏢 createOrganization called:', { name, description, tenantType });

    // Get current user and set in offline proxy
    const { data: { user } } = await offlineSupabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    offlineSupabase.setUserId(user.id);

    // Use the EXACT same function as onboarding flow
    const { data: result, error } = await offlineSupabase
      .rpc("complete_onboarding_with_workspace", {
        p_user_id: user.id,
        p_workspace_name: name,
        p_workspace_description: description || null,
        p_workspace_type: tenantType,
        p_profile_data: {}
      });

    if (error) throw error;

    // Return the organization data from the result (same format as onboarding)
    if (result?.success && result?.tenant_id) {
      const organization = {
        id: result.tenant_id,
        name: result.workspace_name || name,
        description: description || "",
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Trigger Teable onboarding asynchronously (don't block)
      triggerTeableOnboarding(result.tenant_id, organization.name).catch((error) => {
        console.error('Teable onboarding error (non-blocking):', error);
      });

      return organization;
    }

    return null;
  } catch (error) {
    console.error("Error creating organization:", error);
    throw error;
  }
};

/**
 * Get all organizations for the current user
 * Queries base_tenants based on:
 * 1. base_organisation_members (organization memberships)
 * 2. base_profiles.tenant_id (personal workspaces)
 */
export const getUserOrganizations = async (): Promise<{
  organizations: Organization[];
  membership: OrganizationMember[];
}> => {
  try {
    console.log('[OrganizationService] 🔍 getUserOrganizations() called');

    // Get current user ID
    const { data: { user } } = await offlineSupabase.auth.getUser();
    const userId = user?.id;
    console.log('[OrganizationService] Current user ID:', userId);
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Set user ID in offline proxy
    offlineSupabase.setUserId(userId);

    const organizationIds = new Set<string>();
    const membershipMap = new Map<string, { role: MemberRole; created_at: string }>();

    // Step 1: Get all organization IDs where user is a member
    // Use regular supabase to ensure network request is made
    const { data: memberships, error: membershipsError } = await supabase
      .schema('altzero')
      .from('base_organisation_members')
      .select('organisation_id, role, created_at, is_active')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (membershipsError) {
      console.error('[OrganizationService] Error fetching memberships:', membershipsError);
      throw membershipsError;
    }

    console.log('[OrganizationService] Found memberships:', memberships);

    // Helper function to map database roles to MemberRole type
    const mapRoleToMemberRole = (dbRole: string): MemberRole => {
      // Map org_admin to admin for UI compatibility
      if (dbRole === 'org_admin' || dbRole === 'admin') return 'admin';
      // Keep member role as is
      if (dbRole === 'member') return 'member';
      // Default to member for unknown roles
      return 'member';
    };

    // Add organization IDs from memberships
    if (memberships && memberships.length > 0) {
      memberships.forEach((m) => {
        organizationIds.add(m.organisation_id);
        membershipMap.set(m.organisation_id, {
          role: mapRoleToMemberRole(m.role),
          created_at: m.created_at,
        });
      });
    }

    // Step 2: Get personal workspace from base_profiles (if not already in memberships)
    // Use regular supabase to ensure network request is made
    const { data: profile, error: profileError } = await supabase
      .schema('altzero')
      .from('base_profiles')
      .select('tenant_id, created_at')
      .eq('id', userId)
      .not('tenant_id', 'is', null)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      // PGRST116 is "not found" error, which is okay
      console.warn('[OrganizationService] Error fetching profile:', profileError);
    }

    console.log('[OrganizationService] Found profile:', profile);

    // Add personal workspace tenant_id if it exists and not already in memberships
    if (profile?.tenant_id && !organizationIds.has(profile.tenant_id)) {
      organizationIds.add(profile.tenant_id);
      // Personal workspace gets admin role (mapped from org_admin)
      membershipMap.set(profile.tenant_id, {
        role: 'admin',
        created_at: profile.created_at || new Date().toISOString(),
      });
      console.log('[OrganizationService] Added personal workspace:', profile.tenant_id);
    }

    if (organizationIds.size === 0) {
      console.log('[OrganizationService] No organizations found, returning empty arrays');
      return {
        organizations: [],
        membership: [],
      };
    }

    // Step 3: Query base_tenants for all organization IDs
    // Use regular supabase client to ensure network request is made
    const tenantIdsArray = Array.from(organizationIds);
    const { data: tenants, error: tenantsError } = await supabase
      .schema('altzero')
      .from('base_tenants')
      .select('tenant_id, tenant_name, tenant_type, metadata, created_at, updated_at, is_active')
      .in('tenant_id', tenantIdsArray)
      .eq('is_active', true);

    if (tenantsError) {
      console.error('[OrganizationService] Error fetching tenants:', tenantsError);
      throw tenantsError;
    }

    console.log('[OrganizationService] Found tenants:', tenants);

    // Step 4: Transform tenants to organizations and create membership data
    // Normalize tenant_type: 'organisation' -> 'organization' for consistency
    const normalizeTenantType = (type: string | null | undefined): 'personal' | 'organization' => {
      if (!type) return 'organization'; // Default to organization if null/undefined
      if (type === 'personal') return 'personal';
      if (type === 'organisation' || type === 'organization') return 'organization';
      return 'organization'; // Default fallback
    };

    const organizations: Organization[] = (tenants || []).map((tenant: any) => {
      const normalizedType = normalizeTenantType(tenant.tenant_type);
      console.log(`[OrganizationService] Normalizing tenant ${tenant.tenant_name}: "${tenant.tenant_type}" -> "${normalizedType}"`);
      
      return {
        id: tenant.tenant_id,
        name: tenant.tenant_name,
        description: tenant.metadata?.description || '',
        tenant_type: normalizedType,
        created_at: tenant.created_at,
        updated_at: tenant.updated_at || tenant.created_at,
        created_by: tenant.metadata?.created_by || userId,
      };
    });

    // Create membership data by matching tenants with memberships
    const membership: OrganizationMember[] = (tenants || []).map((tenant: any) => {
      const membershipData = membershipMap.get(tenant.tenant_id);
      return {
        id: `membership-${tenant.tenant_id}`,
        organisation_id: tenant.tenant_id,
        user_id: userId,
        role: membershipData?.role || 'member',
        created_at: membershipData?.created_at || tenant.created_at,
      };
    });

    console.log('[OrganizationService] Returning:', {
      organizationsCount: organizations.length,
      membershipCount: membership.length,
      organizationIds: tenantIdsArray,
      organizations,
      membership,
    });

    return {
      organizations,
      membership,
    };
  } catch (error) {
    console.error("Error fetching user organizations:", error);
    throw error;
  }
};

/**
 * Get a single organization by ID
 */
export const getOrganization = async (
  id: string
): Promise<Organization | null> => {
  try {
    const { data, error } = await offlineSupabase
      .from("base_tenants")
      .select("tenant_id, tenant_name, metadata, created_at, updated_at")
      .eq("tenant_id", id)
      .eq("tenant_type", "organization")
      .single();

    if (error) throw error;
    return {
      id: data.tenant_id,
      name: data.tenant_name,
      description: data.metadata?.description || "",
      created_by: data.metadata?.created_by || "",
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  } catch (error) {
    console.error(`Error fetching organization with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Get all members of an organization
 */
export const getOrganizationMembers = async (
  organizationId: string
): Promise<OrganizationMember[]> => {
  try {
    console.log(`[OrganizationService] 🔍 Fetching members for organization: ${organizationId}`);

    // Use RPC function instead of direct table query to bypass RLS
    const { data: membersData, error: membersError } = await offlineSupabase
      .rpc("get_organization_members", { org_id: organizationId });

    if (membersError) {
      console.error(`[OrganizationService] ❌ Error fetching members:`, membersError);
      throw membersError;
    }

    if (!membersData || membersData.length === 0) {
      console.log(`[OrganizationService] ℹ️ No members found for organization ${organizationId}`);
      return [];
    }

    console.log(`[OrganizationService] ✅ Found ${membersData.length} members`);

    // Get user profiles for all members
    const userIds = membersData.map((member: any) => member.user_id);

    // Try to get profiles using individual RPC calls (more reliable than bulk access)
    let profilesData: any[] = [];
    try {
      console.log(`[OrganizationService] 👤 Fetching profiles for ${userIds.length} members...`);

      // Use Promise.allSettled to get as many profiles as possible, even if some fail
      const profilePromises = userIds.map(async (userId: any) => {
        try {
          const { data, error } = await supabase
            .rpc("get_user_profile", { user_id: userId });

          if (!error && data && data.length > 0) {
            const profile = data[0]; // RPC returns array
            return {
              id: userId,
              name: profile.full_name || profile.username,
              full_name: profile.full_name,
              email: profile.email
            };
          }
          return null;
        } catch (error) {
          console.warn(`Failed to get profile for user ${userId}:`, error);
          return null;
        }
      });

      const profileResults = await Promise.allSettled(profilePromises);
      profilesData = profileResults
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .map(result => (result as PromiseFulfilledResult<any>).value);

      console.log(`[OrganizationService] ✅ Successfully fetched ${profilesData.length} out of ${userIds.length} profiles`);

    } catch (error) {
      console.error("[OrganizationService] Error fetching profiles:", error);
      // Continue without profile data
      profilesData = [];
    }

    // Helper function to map database roles to UI roles
    const mapRole = (dbRole: string): string => {
      // Map org_admin to admin for UI compatibility
      if (dbRole === 'org_admin') return 'admin';
      // Keep member role as is
      if (dbRole === 'member') return 'member';
      // For any other roles, return as is
      return dbRole;
    };

    // Combine the data
    const members = membersData.map((member: any) => {
      const profile = profilesData?.find((p) => p.id === member.user_id);
      const mappedRole = mapRole(member.role);

      return {
        id: member.id,
        organisation_id: member.organisation_id,
        user_id: member.user_id,
        role: mappedRole, // Use mapped role for UI display
        created_at: member.created_at,
        full_name: profile?.name || profile?.full_name || `User ${member.user_id.slice(0, 8)}`,
        email: profile?.email || "No email available",
        avatar_url: undefined, // Set to undefined since column doesn't exist
      };
    });

    console.log(`[OrganizationService] 📊 Returning ${members.length} members with roles:`,
      members.map(m => ({ role: m.role, name: m.full_name })));

    return members;
  } catch (error) {
    console.error(
      `[OrganizationService] ❌ Error fetching members for organization ${organizationId}:`,
      error
    );
    throw error;
  }
};

/**
 * Check if the current user is an admin of the organization
 */
export const isOrganizationAdmin = async (
  organizationId: string
): Promise<boolean> => {
  try {
    const { data, error } = await offlineSupabase
      .rpc("is_org_admin", {
        p_org_id: organizationId,
      });

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error(
      `Error checking admin status for organization ${organizationId}:`,
      error
    );
    throw error;
  }
};

/**
 * Invite a user to an organization
 */
export const inviteToOrganization = async (
  organizationId: string,
  email: string,
  role: MemberRole = "member"
): Promise<OrganizationInvitation | null> => {
  try {
    const { data, error } = await offlineSupabase
      .rpc("invite_to_organisation", {
        org_id: organizationId,
        user_email: email,
        user_role: role,
      });

    if (error) throw error;

    // Fetch the invitation details
    if (data) {
      const { data: invitationData, error: invitationError } = await offlineSupabase
        .from("base_organisation_invitations")
        .select("*")
        .eq("id", data)
        .single();

      if (invitationError) throw invitationError;
      return invitationData;
    }

    return null;
  } catch (error) {
    console.error("Error inviting user to organization:", error);
    throw error;
  }
};

/**
 * Get all invitations for an organization
 */
export const getOrganizationInvitations = async (
  organizationId: string
): Promise<OrganizationInvitation[]> => {
  try {
    const { data, error } = await offlineSupabase
      .from("base_organisation_invitations")
      .select("*")
      .eq("organisation_id", organizationId);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error(
      `Error fetching invitations for organization ${organizationId}:`,
      error
    );
    throw error;
  }
};

/**
 * Accept an organization invitation
 */
export const acceptOrganizationInvitation = async (
  invitationId: string
): Promise<boolean> => {
  try {
    const { data, error } = await offlineSupabase
      .rpc("accept_org_invitation", {
        p_invitation_id: invitationId,
      });

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error(`Error accepting invitation ${invitationId}:`, error);
    throw error;
  }
};

/**
 * Get all pending invitations for the current user
 */
export const getUserPendingOrganizationInvitations = async (): Promise<
  OrganizationInvitation[]
> => {
  try {
    const { data, error } = await offlineSupabase
      .rpc("get_user_pending_org_invitations");

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching user organization invitations:", error);
    throw error;
  }
};

/**
 * Update an organization
 */
export const updateOrganization = async (
  organizationId: string,
  name: string,
  description?: string
): Promise<Organization | null> => {
  try {
    console.log(`[OrganizationService] 🔄 Updating organization: ${organizationId}`);

    // Use RPC function to bypass RLS restrictions
    const { data, error } = await supabase
      .rpc("update_organization", {
        p_org_id: organizationId,
        p_name: name,
        p_description: description || null,
      });

    if (error) {
      console.error(`[OrganizationService] ❌ RPC update failed:`, error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.error(`[OrganizationService] ❌ No data returned from update`);
      throw new Error("Organization not found or update failed");
    }

    const orgData = data[0]; // RPC returns array
    console.log(`[OrganizationService] ✅ Organization updated successfully: ${orgData.tenant_name}`);

    return {
      id: orgData.tenant_id,
      name: orgData.tenant_name,
      description: orgData.metadata?.description || "",
      created_by: orgData.metadata?.created_by || "",
      created_at: orgData.created_at,
      updated_at: orgData.updated_at
    };
  } catch (error) {
    console.error("Error updating organization:", error);
    throw error;
  }
};

/**
 * Delete an organization
 */
export const deleteOrganization = async (
  organizationId: string
): Promise<boolean> => {
  try {
    console.log(`[OrganizationService] 🗑️ Deleting organization: ${organizationId}`);

    // Use RPC function to bypass RLS restrictions
    const { data, error } = await supabase
      .rpc("delete_organization", {
        p_org_id: organizationId,
      });

    if (error) {
      console.error(`[OrganizationService] ❌ RPC delete failed:`, error);
      throw error;
    }

    console.log(`[OrganizationService] ✅ Organization deletion result: ${data}`);
    return data === true;
  } catch (error) {
    console.error("Error deleting organization:", error);
    throw error;
  }
};

/**
 * Remove a member from an organization
 */
export const removeOrganizationMember = async (
  organizationId: string,
  userId: string
): Promise<boolean> => {
  try {
    console.log('[OrganizationService] 🚪 removeOrganizationMember called:', { organizationId, userId });
    
    const { data, error } = await offlineSupabase
      .rpc("remove_organization_member", {
        p_org_id: organizationId,
        p_user_id: userId
      });

    if (error) {
      console.error('[OrganizationService] ❌ RPC error:', error);
      throw error;
    }

    console.log('[OrganizationService] ✅ Member removed successfully:', data);
    return data;
  } catch (error) {
    console.error("Error removing organization member:", error);
    throw error;
  }
};

/**
 * Leave organization (self-removal)
 */
export const leaveOrganization = async (
  organizationId: string
): Promise<boolean> => {
  try {
    console.log('[OrganizationService] 🚪 leaveOrganization called:', { organizationId });
    
    // Get current user ID
    const { data: { user } } = await offlineSupabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    return await removeOrganizationMember(organizationId, user.id);
  } catch (error) {
    console.error("Error leaving organization:", error);
    throw error;
  }
};

/**
 * Reject an organization invitation
 */
export const rejectOrganizationInvitation = async (
  invitationId: string
): Promise<boolean> => {
  try {
    const { error } = await offlineSupabase
      .from("base_organisation_invitations")
      .update({ status: "rejected", updated_at: new Date().toISOString() })
      .eq("id", invitationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error(`Error rejecting invitation ${invitationId}:`, error);
    throw error;
  }
};
